import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useQueryClient } from '@tanstack/react-query';
import { useFinalizeJobEnd, useAcknowledgeDelivery, jobEndKeys } from '@/queries/useJobEnds';
import type { RecordAccess } from '@/auth/permissions.types';
import type { JobEndDetail } from '@/api/types/jobEnd.types';
import { validateJobEnd, NO_ERRORS } from './jobEndValidation';
import type { ValidationErrors } from './jobEndValidation';
import { formatDate } from '@/utils/dateFormatter';
import { getErrorMessage } from '@/utils/errorMessage';

interface JobEndFooterProps {
  jobEndId: number;
  permissions: RecordAccess | undefined;
  jobEnd: JobEndDetail;
  showNatureOfRole: boolean;
  showSkillRatings: boolean;
  setSnackbar: (snackbar: { open: boolean; message: string; severity: 'success' | 'error' }) => void;
  setValidationErrors: (errors: ValidationErrors) => void;
  onDownloadPdf?: () => void;
  isGeneratingPdf?: boolean;
}

export function JobEndFooter({
  jobEndId,
  permissions,
  jobEnd,
  showNatureOfRole,
  showSkillRatings,
  setSnackbar,
  setValidationErrors,
  onDownloadPdf,
  isGeneratingPdf = false,
}: JobEndFooterProps) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [acknowledgeDialogOpen, setAcknowledgeDialogOpen] = useState(false);

  const { mutate: finalize, isPending: isFinalizing } = useFinalizeJobEnd();
  const { mutate: acknowledge, isPending: isAcknowledging } = useAcknowledgeDelivery();
  const canSave = permissions?.actions.canSave || false;
  const canMarkAsFinal = permissions?.actions.canMarkAsFinal || false;
  const submitBlockedByGate = permissions?.actions.submitBlockedByGate || false;
  const canAcknowledge = permissions?.actions.canAcknowledge || false;
  const canDownload = permissions?.actions.canDownload || false;

  const hasAnyAction = canSave || canMarkAsFinal || submitBlockedByGate || canAcknowledge || canDownload;

  const submitGateTooltip = jobEnd.submissionGateOpensOn
    ? `Submission opens on ${formatDate(jobEnd.submissionGateOpensOn)} (2 working days after the assignment end date).`
    : 'Submission is not yet available. It opens 2 working days after the assignment end date.';

  const handleSave = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setSnackbar({ open: true, message: 'Saved', severity: 'success' });
  };

  const handleMarkAsFinal = async () => {
    await queryClient.refetchQueries({ queryKey: jobEndKeys.detail(String(jobEndId)) });
    const freshJobEnd = queryClient.getQueryData<JobEndDetail>(jobEndKeys.detail(String(jobEndId))) || jobEnd;
    const errors = validateJobEnd(freshJobEnd, showNatureOfRole, showSkillRatings);
    setValidationErrors(errors);

    if (errors.natureOfRole || errors.skillRatings || errors.strengthsDev) return;

    setFinalizeDialogOpen(true);
  };

  const handleFinalize = () => {
    setFinalizeDialogOpen(false);
    finalize({ id: jobEndId, rowVersion: jobEnd.rowVersion }, {
      onSuccess: () => {
        setValidationErrors(NO_ERRORS);
        setSnackbar({
          open: true,
          message: 'Job End has been finalised and released to the appraisee.',
          severity: 'success',
        });
      },
      // No fabricated section errors here: the server rejects a submission for reasons the banner
      // cannot express (concurrency, the submission gate, a network failure), and asserting
      // "3 strengths required" would contradict the message in the snackbar.
      onError: (error) => {
        setSnackbar({
          open: true,
          message: getErrorMessage(error),
          severity: 'error',
        });
      },
    });
  };

  const handleAcknowledge = () => {
    setAcknowledgeDialogOpen(false);
    acknowledge({ id: jobEndId, rowVersion: jobEnd.rowVersion }, {
      onSuccess: () => {
        setSnackbar({
          open: true,
          message: 'Job End delivery has been acknowledged.',
          severity: 'success',
        });
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: getErrorMessage(error),
          severity: 'error',
        });
      },
    });
  };

  if (!hasAnyAction) return null;

  const isPending = isFinalizing || isAcknowledging || isGeneratingPdf;

  return (
    <>
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: theme.zIndex.appBar - 1,
          backgroundColor: theme.palette.custom.darkTeal,
          px: 3,
          py: 1.5,
          mx: { xs: -2, md: -3 },
          mb: { xs: -2, md: -3 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {canSave && (
            <Button
              variant="contained"
              color="success"
              onClick={handleSave}
              disabled={isPending}
            >
              Save
            </Button>
          )}
          {canDownload && (
            <Button
              variant="contained"
              color="success"
              startIcon={<FileDownloadIcon />}
              onClick={onDownloadPdf}
              disabled={isPending}
            >
              {isGeneratingPdf ? 'Generating...' : 'Download PDF'}
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {canMarkAsFinal && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<SendIcon />}
              onClick={handleMarkAsFinal}
              disabled={isPending}
            >
              Mark as final and submit
            </Button>
          )}
          {submitBlockedByGate && (
            <Tooltip title={submitGateTooltip}>
              <span>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<SendIcon />}
                  disabled
                  aria-label={submitGateTooltip}
                >
                  Mark as final and submit
                </Button>
              </span>
            </Tooltip>
          )}
          {canAcknowledge && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<CheckIcon />}
              onClick={() => setAcknowledgeDialogOpen(true)}
              disabled={isPending}
            >
              Acknowledge delivery
            </Button>
          )}
        </Box>
      </Box>

      <Dialog open={finalizeDialogOpen} onClose={() => setFinalizeDialogOpen(false)}>
        <DialogTitle>Submit Job End</DialogTitle>
        <DialogContent>
          <Typography>
            Once submitted, you will no longer be able to edit the form, and it will be released to the appraisee.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={() => setFinalizeDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleFinalize}
            disabled={isFinalizing}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={acknowledgeDialogOpen} onClose={() => setAcknowledgeDialogOpen(false)}>
        <DialogTitle>Acknowledge Delivery</DialogTitle>
        <DialogContent>
          <Typography>
            By selecting Acknowledge Delivery, you confirm that your Job End delivery discussion has taken place.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={() => setAcknowledgeDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAcknowledge}
            disabled={isAcknowledging}
          >
            Acknowledge Delivery
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
